import React, { useState, useEffect } from 'react';
import { presupuestosAPI, clientesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  FileText,
  Download,
  Send,
  CheckCircle,
  XCircle,
  Eye,
  X,
  Copy
} from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor, translateEstado } from '../lib/utils';

const PresupuestosPage = () => {
  const [presupuestos, setPresupuestos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPresupuesto, setSelectedPresupuesto] = useState(null);
  const [formData, setFormData] = useState({
    cliente_id: '',
    titulo: '',
    descripcion: '',
    items: [],
    subtotal: 0,
    iva_percent: 21,
    iva_amount: 0,
    total: 0,
    estado: 'borrador',
    validez_dias: 30,
    notas: '',
  });
  const [newItem, setNewItem] = useState({ descripcion: '', cantidad: 1, precio_unitario: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [presupuestosRes, clientesRes] = await Promise.all([
        presupuestosAPI.list(),
        clientesAPI.list(),
      ]);
      setPresupuestos(presupuestosRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (items, ivaPercent = 21) => {
    const subtotal = items.reduce((sum, item) => sum + (item.cantidad * item.precio_unitario), 0);
    const ivaAmount = subtotal * ivaPercent / 100;
    const total = subtotal + ivaAmount;
    return { subtotal, iva_amount: ivaAmount, total };
  };

  const addItem = () => {
    if (!newItem.descripcion || newItem.precio_unitario <= 0) {
      toast.error('Completa la descripción y precio del item');
      return;
    }
    const updatedItems = [...formData.items, { ...newItem }];
    const totals = calculateTotals(updatedItems, formData.iva_percent);
    setFormData({
      ...formData,
      items: updatedItems,
      ...totals,
    });
    setNewItem({ descripcion: '', cantidad: 1, precio_unitario: 0 });
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    const totals = calculateTotals(updatedItems, formData.iva_percent);
    setFormData({
      ...formData,
      items: updatedItems,
      ...totals,
    });
  };

  const handleIvaChange = (newIva) => {
    const totals = calculateTotals(formData.items, newIva);
    setFormData({
      ...formData,
      iva_percent: newIva,
      ...totals,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedPresupuesto) {
        await presupuestosAPI.update(selectedPresupuesto.id, formData);
        toast.success('Presupuesto actualizado');
      } else {
        await presupuestosAPI.create(formData);
        toast.success('Presupuesto creado');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar presupuesto');
    }
  };

  const handleEdit = (presupuesto) => {
    setSelectedPresupuesto(presupuesto);
    setFormData({
      cliente_id: presupuesto.cliente_id || '',
      titulo: presupuesto.titulo || '',
      descripcion: presupuesto.descripcion || '',
      items: presupuesto.items || [],
      total: presupuesto.total || 0,
      estado: presupuesto.estado || 'borrador',
      validez_dias: presupuesto.validez_dias || 30,
      notas: presupuesto.notas || '',
    });
    setDialogOpen(true);
  };

  const handleChangeEstado = async (presupuesto, nuevoEstado) => {
    try {
      await presupuestosAPI.updateEstado(presupuesto.id, nuevoEstado);
      toast.success(`Estado cambiado a ${translateEstado(nuevoEstado)}`);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async () => {
    try {
      await presupuestosAPI.delete(selectedPresupuesto.id);
      toast.success('Presupuesto eliminado');
      setDeleteDialogOpen(false);
      setSelectedPresupuesto(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar presupuesto');
    }
  };

  const handleDownloadPDF = async (presupuesto) => {
    try {
      toast.loading('Generando PDF...', { id: 'pdf-download' });
      await presupuestosAPI.downloadPDF(presupuesto.id, `presupuesto_${presupuesto.numero}.pdf`);
      toast.success('PDF descargado correctamente', { id: 'pdf-download' });
    } catch (error) {
      toast.error('Error al descargar el PDF', { id: 'pdf-download' });
    }
  };

  const handleDuplicar = async (presupuesto) => {
    try {
      const response = await presupuestosAPI.duplicar(presupuesto.id);
      toast.success(`Presupuesto duplicado: ${response.data.numero}`);
      loadData();
    } catch (error) {
      toast.error('Error al duplicar el presupuesto');
    }
  };

  const resetForm = () => {
    setSelectedPresupuesto(null);
    setFormData({
      cliente_id: '',
      titulo: '',
      descripcion: '',
      items: [],
      subtotal: 0,
      iva_percent: 21,
      iva_amount: 0,
      total: 0,
      estado: 'borrador',
      validez_dias: 30,
      notas: '',
    });
    setNewItem({ descripcion: '', cantidad: 1, precio_unitario: 0 });
  };

  const getClienteName = (clienteId) => {
    const cliente = clientes.find((c) => c.id === clienteId);
    return cliente?.nombre || 'Cliente desconocido';
  };

  const filteredPresupuestos = presupuestos.filter(
    (p) =>
      p.titulo?.toLowerCase().includes(search.toLowerCase()) ||
      p.numero?.toLowerCase().includes(search.toLowerCase()) ||
      getClienteName(p.cliente_id)?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6 animate-fade-in" data-testid="presupuestos-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Presupuestos</h1>
          <p className="text-slate-500 text-sm mt-1">{presupuestos.length} presupuestos</p>
        </div>
        <div className="lg:hidden flex-1">
          <p className="text-xs text-slate-500">{presupuestos.length} presupuestos</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-slate-900 hover:bg-slate-800 h-10 lg:h-9 active:scale-95 transition-transform"
          data-testid="new-presupuesto-btn"
        >
          <Plus className="w-4 h-4 mr-1 lg:mr-2" />
          <span className="hidden sm:inline">Nuevo Presupuesto</span>
          <span className="sm:hidden">Nuevo</span>
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar presupuestos..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-12 lg:h-10 lg:max-w-md"
          data-testid="search-presupuestos-input"
        />
      </div>

      {/* Content */}
      {filteredPresupuestos.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">{search ? 'No se encontraron presupuestos' : 'No hay presupuestos'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="lg:hidden space-y-3">
            {filteredPresupuestos.map((presupuesto) => (
              <Card 
                key={presupuesto.id} 
                className="border-slate-200 active:scale-[0.99] transition-transform"
                data-testid={`presupuesto-card-${presupuesto.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase rounded ${getStatusColor(presupuesto.estado)}`}>
                          {translateEstado(presupuesto.estado)}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">{presupuesto.numero}</span>
                      </div>
                      <p className="font-semibold text-slate-900 truncate">{presupuesto.titulo}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{getClienteName(presupuesto.cliente_id)}</p>
                    </div>
                    <div className="flex flex-col items-end">
                      <p className="text-lg font-bold text-slate-900 font-mono">{formatCurrency(presupuesto.total)}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{formatDate(presupuesto.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 text-xs"
                        onClick={() => { setSelectedPresupuesto(presupuesto); setViewDialogOpen(true); }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 text-xs"
                        onClick={() => handleDownloadPDF(presupuesto)}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-9 w-9" data-testid={`presupuesto-actions-${presupuesto.id}`}>
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(presupuesto)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicar(presupuesto)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicar
                        </DropdownMenuItem>
                        {presupuesto.estado === 'borrador' && (
                          <DropdownMenuItem onClick={() => handleChangeEstado(presupuesto, 'enviado')}>
                            <Send className="w-4 h-4 mr-2" />
                            Marcar Enviado
                          </DropdownMenuItem>
                        )}
                        {presupuesto.estado === 'enviado' && (
                          <>
                            <DropdownMenuItem onClick={() => handleChangeEstado(presupuesto, 'aceptado')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Marcar Aceptado
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeEstado(presupuesto, 'rechazado')}>
                              <XCircle className="w-4 h-4 mr-2" />
                              Marcar Rechazado
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuItem
                          onClick={() => { setSelectedPresupuesto(presupuesto); setDeleteDialogOpen(true); }}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <Card className="border-slate-200 hidden lg:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Número</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Título</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Cliente</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Estado</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Total</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Fecha</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredPresupuestos.map((presupuesto) => (
                      <tr key={presupuesto.id} className="hover:bg-slate-50" data-testid={`presupuesto-row-${presupuesto.id}`}>
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-slate-600">{presupuesto.numero}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-900">{presupuesto.titulo}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {getClienteName(presupuesto.cliente_id)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${getStatusColor(presupuesto.estado)}`}>
                            {translateEstado(presupuesto.estado)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {formatCurrency(presupuesto.total)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {formatDate(presupuesto.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`presupuesto-desktop-actions-${presupuesto.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setSelectedPresupuesto(presupuesto); setViewDialogOpen(true); }}>
                                <Eye className="w-4 h-4 mr-2" />
                                Ver
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(presupuesto)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadPDF(presupuesto)}>
                                <Download className="w-4 h-4 mr-2" />
                                Descargar PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicar(presupuesto)} data-testid="duplicar-presupuesto-btn">
                                <Copy className="w-4 h-4 mr-2" />
                                Duplicar
                              </DropdownMenuItem>
                              {presupuesto.estado === 'borrador' && (
                                <DropdownMenuItem onClick={() => handleChangeEstado(presupuesto, 'enviado')}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Marcar Enviado
                                </DropdownMenuItem>
                              )}
                              {presupuesto.estado === 'enviado' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleChangeEstado(presupuesto, 'aceptado')}>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Marcar Aceptado
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleChangeEstado(presupuesto, 'rechazado')}>
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Marcar Rechazado
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuItem
                                onClick={() => { setSelectedPresupuesto(presupuesto); setDeleteDialogOpen(true); }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="presupuesto-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedPresupuesto ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select
                  value={formData.cliente_id}
                  onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
                  required
                >
                  <SelectTrigger data-testid="presupuesto-cliente-select" className="h-12 lg:h-10">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map((cliente) => (
                      <SelectItem key={cliente.id} value={cliente.id}>
                        {cliente.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Validez (días)</Label>
                <Input
                  type="number"
                  value={formData.validez_dias}
                  onChange={(e) => setFormData({ ...formData, validez_dias: parseInt(e.target.value) || 30 })}
                  data-testid="presupuesto-validez-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Ej: Reforma integral baño"
                required
                data-testid="presupuesto-titulo-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción general del trabajo..."
                rows={2}
                data-testid="presupuesto-descripcion-input"
              />
            </div>

            {/* Items */}
            <div className="space-y-3">
              <Label>Partidas</Label>
              <div className="border border-slate-200 rounded-md p-4 space-y-3">
                {formData.items.length > 0 && (
                  <div className="space-y-2">
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                        <span className="flex-1">{item.descripcion}</span>
                        <span className="text-slate-500">{item.cantidad} x</span>
                        <span className="font-mono">{formatCurrency(item.precio_unitario)}</span>
                        <span className="font-mono font-medium">{formatCurrency(item.cantidad * item.precio_unitario)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeItem(index)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    placeholder="Descripción"
                    value={newItem.descripcion}
                    onChange={(e) => setNewItem({ ...newItem, descripcion: e.target.value })}
                    className="flex-1"
                    data-testid="item-descripcion-input"
                  />
                  <Input
                    type="number"
                    placeholder="Cant."
                    value={newItem.cantidad}
                    onChange={(e) => setNewItem({ ...newItem, cantidad: parseInt(e.target.value) || 1 })}
                    className="w-20"
                    data-testid="item-cantidad-input"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Precio"
                    value={newItem.precio_unitario || ''}
                    onChange={(e) => setNewItem({ ...newItem, precio_unitario: parseFloat(e.target.value) || 0 })}
                    className="w-28"
                    data-testid="item-precio-input"
                  />
                  <Button type="button" variant="outline" onClick={addItem} data-testid="add-item-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* IVA y Totales */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm">IVA aplicable</Label>
                <Select 
                  value={String(formData.iva_percent)}
                  onValueChange={(val) => handleIvaChange(parseFloat(val))}
                >
                  <SelectTrigger className="w-32" data-testid="iva-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (Exento)</SelectItem>
                    <SelectItem value="4">4% (Superreducido)</SelectItem>
                    <SelectItem value="10">10% (Reducido)</SelectItem>
                    <SelectItem value="21">21% (General)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="border-t border-slate-200 pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-mono">{formatCurrency(formData.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">IVA ({formData.iva_percent}%)</span>
                  <span className="font-mono">{formatCurrency(formData.iva_amount)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-slate-300 pt-2">
                  <span>TOTAL</span>
                  <span className="font-mono text-slate-900">{formatCurrency(formData.total)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Notas adicionales..."
                rows={2}
                data-testid="presupuesto-notas-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-presupuesto-btn">
                {selectedPresupuesto ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-xl" data-testid="view-presupuesto-dialog">
          <DialogHeader>
            <DialogTitle>Presupuesto {selectedPresupuesto?.numero}</DialogTitle>
          </DialogHeader>
          {selectedPresupuesto && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Cliente</p>
                  <p className="font-medium">{getClienteName(selectedPresupuesto.cliente_id)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Estado</p>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${getStatusColor(selectedPresupuesto.estado)}`}>
                    {translateEstado(selectedPresupuesto.estado)}
                  </span>
                </div>
                <div>
                  <p className="text-slate-500">Fecha</p>
                  <p className="font-medium">{formatDate(selectedPresupuesto.created_at)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Validez</p>
                  <p className="font-medium">{selectedPresupuesto.validez_dias} días</p>
                </div>
              </div>
              <div>
                <p className="text-slate-500 text-sm mb-1">Título</p>
                <p className="font-medium">{selectedPresupuesto.titulo}</p>
              </div>
              {selectedPresupuesto.descripcion && (
                <div>
                  <p className="text-slate-500 text-sm mb-1">Descripción</p>
                  <p>{selectedPresupuesto.descripcion}</p>
                </div>
              )}
              {selectedPresupuesto.items?.length > 0 && (
                <div>
                  <p className="text-slate-500 text-sm mb-2">Partidas</p>
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2">Descripción</th>
                          <th className="text-right px-3 py-2">Cant.</th>
                          <th className="text-right px-3 py-2">Precio</th>
                          <th className="text-right px-3 py-2">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedPresupuesto.items.map((item, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2">{item.descripcion}</td>
                            <td className="text-right px-3 py-2">{item.cantidad}</td>
                            <td className="text-right px-3 py-2 font-mono">{formatCurrency(item.precio_unitario)}</td>
                            <td className="text-right px-3 py-2 font-mono">{formatCurrency(item.cantidad * item.precio_unitario)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="flex justify-end p-3 bg-slate-50 rounded-md">
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(selectedPresupuesto.total)}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar presupuesto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el presupuesto
              "{selectedPresupuesto?.numero}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-presupuesto-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PresupuestosPage;
